import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, CheckCircle2, Mail, DraftingCompass, FileCheck2, Ruler, PenLine } from "lucide-react";
import { MaskedLines, Reveal, ChapterHeading } from "@/components/motion";
import JsonLd from "@/components/JsonLd";
import { EMAIL } from "@/constants/site";
import { MODALITIES } from "@/constants/modalities";
import { usePageMeta } from "@/hooks/usePageMeta";

const STEPS = [
  { icon: DraftingCompass, title: "Floor plans & equipment", body: "Your architectural drawings and the exact equipment going into the room — make, model, and tube orientation drive everything downstream." },
  { icon: Ruler, title: "Workload & occupancy", body: "Weekly patient volume, use factors, and who occupies each adjacent space — offices, corridors, waiting rooms, floors above and below." },
  { icon: FileCheck2, title: "NCRP 147 calculations", body: "Primary, scatter, and leakage barrier calculations under NCRP Report No. 147 — the methodology state reviewers expect to see." },
  { icon: PenLine, title: "Signed design report", body: "A wall-by-wall barrier schedule, marked-up floor plans, and the physicist's signature — ready for your contractor and your state." },
];

const WHEN = [
  { title: "New construction or renovation", body: "Most states require a physicist's shielding design before walls go up — retrofitting lead after the fact is the expensive path." },
  { title: "New or replacement equipment", body: "A new CT, fluoroscopy unit, or CBCT changes workload and scatter — your existing design may no longer cover the room." },
  { title: "State registration or plan review", body: "Many state radiation control programs require the shielding design on file — or submitted for review — before the room can be used." },
  { title: "Room repurposing", body: "Turning a general x-ray room into a CT suite (or vice versa) almost always changes what the barriers must stop." },
];

const FAQS = [
  {
    q: "What is radiation shielding design?",
    a: "Shielding design is the physics work that determines how thick the walls, doors, floors, and windows of an x-ray or imaging room need to be. A qualified physicist calculates barrier thicknesses from your equipment, workload, and floor plan — following NCRP Report No. 147 — and documents the answer in a signed report your state radiation control program can review.",
  },
  {
    q: "Is a shielding design the same thing as a shielding plan?",
    a: "Yes — the terms are used interchangeably. Some states say 'shielding plan,' others say 'shielding design' or 'shielding evaluation.' All refer to the physicist-prepared report specifying barrier thicknesses for the room. What matters is that it follows NCRP 147 methodology and is signed by a qualified physicist.",
  },
  {
    q: "How much does a shielding design cost?",
    a: "$950 per room, flat. That includes the barrier calculations, marked-up floor plans, the signed report, and direct support if your state reviewer has questions about the design.",
  },
  {
    q: "Who is allowed to prepare a shielding design?",
    a: "Requirements vary by state, but most expect a qualified medical or health physicist — typically board-certified or state-approved. Every Heartland Physics shielding design is prepared and signed directly by Lancer Smith, M.S., an approved consulting physicist.",
  },
  {
    q: "How long does a shielding design take?",
    a: "Most designs are delivered within five business days of receiving complete project information — facility details, floor plans, and equipment specs through the online intake.",
  },
  {
    q: "Which rooms need a shielding design?",
    a: "In most states, any room with x-ray producing equipment: general radiography, CT, fluoroscopy and C-arms, mammography, dental and CBCT, and veterinary imaging rooms. Small offices are not exempt — dental and veterinary rooms are among the most common projects.",
  },
];

const ShieldingDesign = () => {
  usePageMeta(
    "Radiation Shielding Design for X-Ray, CT & Imaging Rooms | Heartland Physics",
    "Physicist-prepared radiation shielding design for diagnostic imaging: NCRP 147 barrier calculations, marked-up floor plans, and a state-ready signed report — $950 per room, five business days."
  );
  return (
    <>
      <JsonLd
        id="service-shielding-design"
        data={{
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Radiation Shielding Design",
          description: "Physicist-prepared radiation shielding designs for diagnostic imaging rooms — NCRP 147 barrier calculations, material specifications, and state-ready documentation.",
          provider: { "@type": "ProfessionalService", name: "Heartland Physics", url: "https://heartlandphysics.com/" },
          areaServed: "United States",
          offers: { "@type": "Offer", price: "950", priceCurrency: "USD" },
        }}
      />
      <JsonLd
        id="faq-shielding-design"
        data={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }}
      />
      <JsonLd
        id="breadcrumb-shielding-design"
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Heartland Physics", item: "https://heartlandphysics.com/" },
            { "@type": "ListItem", position: 2, name: "Shielding Design", item: "https://heartlandphysics.com/shielding-design" },
          ],
        }}
      />

      <section data-testid="shielding-design-hero" className="border-b border-border bg-blueprint-grid pt-16">
        <div className="mx-auto max-w-7xl px-5 pb-14 pt-14 md:px-8 md:pb-20 md:pt-20">
          <Reveal y={12}>
            <p className="eyebrow">Shielding Design</p>
          </Reveal>
          <h1 className="mt-6 max-w-3xl font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            <MaskedLines delay={0.1} lines={["Radiation shielding design", "for diagnostic imaging rooms."]} />
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-ink/70 md:text-lg" data-testid="shielding-design-intro">
              Shielding design is the physics behind a safe imaging room: how thick every wall, door,
              and window must be before your x-ray, CT, or fluoroscopy equipment can go in. Heartland
              Physics prepares yours directly — calculated under NCRP 147, signed by the physicist,
              and ready for your state reviewer.
            </p>
          </Reveal>
          <Reveal delay={0.65} className="mt-9 flex flex-wrap items-center gap-4">
            <Link
              to="/request"
              data-testid="shielding-design-cta-request"
              className="group flex items-center gap-2 bg-blueprint px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-ink"
            >
              Request a shielding design
              <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <a
              href={`mailto:${EMAIL}`}
              data-testid="shielding-design-cta-email"
              className="group flex items-center gap-2 border border-ink/25 bg-white/60 px-7 py-4 text-sm font-semibold text-ink transition-colors duration-200 hover:border-blueprint hover:text-blueprint"
            >
              <Mail size={16} /> Email Lancer@HeartlandPhysics.com
            </a>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-border bg-white" data-testid="shielding-design-what">
        <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
          <ChapterHeading index="01" eyebrow="What it is" title="What a shielding design answers" />
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-3xl text-base leading-relaxed text-ink/70 md:text-lg">
              Before an imaging room is built or new equipment goes in, someone has to answer a precise
              physics question: <span className="font-semibold text-ink">how much shielding does each barrier need?</span> A
              shielding design answers it with math — workload, use and occupancy factors, scatter and
              leakage calculations — and documents the answer so your state radiation control program can
              review it. Done right, it protects your patients, your staff, your neighbors, and your license.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.05} className="h-full">
                <div className="flex h-full flex-col bg-white p-6">
                  <s.icon size={18} className="text-blueprint" />
                  <h3 className="mt-4 font-heading text-base font-bold tracking-tight text-ink">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/60">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-paper" data-testid="shielding-design-modalities">
        <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
          <ChapterHeading index="02" eyebrow="By modality" title="Shielding design for every room type" />
          <div className="mt-12 grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
            {MODALITIES.map((m, i) => (
              <Reveal key={m.slug} delay={i * 0.05} className="h-full">
                <Link to={`/shielding-design/${m.slug}`} data-testid={`shielding-design-modality-${m.slug}`} className="group flex h-full flex-col bg-white p-6 transition-colors duration-300 hover:bg-ink">
                  <h3 className="font-heading text-base font-bold tracking-tight text-ink transition-colors group-hover:text-white">{m.name} shielding design</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-ink/60 transition-colors group-hover:text-white/70">{m.short}</p>
                  <span className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-blueprint transition-colors group-hover:text-azure">
                    Requirements & process <ArrowRight size={13} className="transition-transform duration-200 group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-white" data-testid="shielding-design-when">
        <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
          <ChapterHeading index="03" eyebrow="Timing" title="When a shielding design is required" />
          <div className="mt-12 grid gap-px border border-border bg-border sm:grid-cols-2">
            {WHEN.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.06} className="h-full">
                <div className="h-full bg-white p-7">
                  <p className="font-mono text-xs text-blueprint">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-3 font-heading text-base font-bold tracking-tight text-ink">{w.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/60">{w.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-3xl text-sm leading-relaxed text-ink/60">
              Not sure what your state asks for? Check your{" "}
              <Link to="/shielding-requirements" className="font-semibold text-blueprint hover:text-ink">state's shielding requirements</Link>
              {" "}— we track the agency, the forms, and the timelines for all 50 states. Ready for the document
              itself? See what a{" "}
              <Link to="/shielding-plan" className="font-semibold text-blueprint hover:text-ink">shielding plan</Link>
              {" "}includes and how delivery works.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-border bg-paper" data-testid="shielding-design-faq">
        <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
          <ChapterHeading index="04" eyebrow="Questions" title="Shielding design FAQ" />
          <div className="mt-10 max-w-3xl divide-y divide-border border-y border-border">
            {FAQS.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.05}>
                <div className="py-6" data-testid={`shielding-design-faq-${i}`}>
                  <h3 className="font-heading text-base font-bold tracking-tight text-ink">{f.q}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-ink/65">{f.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink bg-blueprint-grid-dark" data-testid="shielding-design-final-cta">
        <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
          <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-end">
            <div>
              <p className="eyebrow !text-azure">Flat rate · 5 business days</p>
              <h2 className="mt-4 max-w-xl font-heading text-3xl font-extrabold tracking-tight text-white md:text-4xl">
                <MaskedLines lines={["Your room. Your design.", "$950, signed by the physicist."]} />
              </h2>
            </div>
            <Reveal delay={0.2} className="flex flex-wrap gap-4">
              <Link
                to="/request"
                data-testid="shielding-design-final-request"
                className="group flex items-center gap-2 bg-white px-7 py-4 text-sm font-semibold text-ink transition-colors duration-200 hover:bg-azure hover:text-white"
              >
                Start your request
                <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <a
                href={`mailto:${EMAIL}`}
                data-testid="shielding-design-final-email"
                className="flex items-center gap-2 border border-white/30 px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:border-azure hover:text-azure"
              >
                <Mail size={16} /> Email Lancer@HeartlandPhysics.com
              </a>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
};

export default ShieldingDesign;
